/* eslint-disable jsx-a11y/anchor-is-valid */
import { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom'
import { KTIcon } from '../../../helpers'
import { formatNavbarBadge } from '../../../helpers/navbarBadge'
import { useIntl } from 'react-intl'
import { Tooltip } from 'bootstrap'

import {
    clearState,
    dismissNotification,
    getNotifications,
    getUnreadNotifications,
    markAllNotificationsAsRead,
    patchNotification
} from '../../../../app/pages/notifications/notificationActions';
import { groupNotificationsByTime } from '../../../../app/pages/notifications/notificationGrouping';

// Trigger-type icon mapping. Falls back to a generic bell when the
// trigger is unknown — keeps the menu graceful when new triggers land.
const TRIGGER_ICON: Record<number, { icon: string; tone: string }> = {
    1:  { icon: 'profile-user', tone: 'primary' },   // NewDealerSignUp
    2:  { icon: 'profile-user', tone: 'primary' },   // NewDealerWelcome
    3:  { icon: 'key',          tone: 'warning' },   // ForgotPassword
    4:  { icon: 'shield-tick',  tone: 'success' },   // UserApproved
    5:  { icon: 'document',     tone: 'info' },      // InvoiceNotificationRequested
    6:  { icon: 'document',     tone: 'info' },      // QuoteNotificationRequested
    7:  { icon: 'check-circle', tone: 'success' },   // OrderApproved
    8:  { icon: 'gear',         tone: 'info' },      // OrderPreparing
    9:  { icon: 'package',      tone: 'success' },   // OrderReady
    10: { icon: 'rocket',       tone: 'info' },      // OrderShipped
    11: { icon: 'check-square', tone: 'success' },   // OrderDelivered
    12: { icon: 'cross-circle', tone: 'danger' },    // OrderCancelled
    13: { icon: 'dollar',       tone: 'success' },   // OrderPaymentReceived
    14: { icon: 'warning-2',    tone: 'warning' },   // LowStockAlert
    15: { icon: 'chart-line',   tone: 'primary' },   // DailyBusinessReport
};

type Notification = {
    id: number;
    triggerType: number;
    subject: string;
    body: string;
    redirect: string;
    isRead: boolean;
    createdOn: string;
    createdOnString: string;
};

const HeaderNotificationsMenu = (props: any) => {
    const intl = useIntl();
    const [tab, setTab] = useState<'unread' | 'all'>('unread');

    const {
        notifications = [],
        unreadNotifications = [],
        clearState,
        getNotifications,
        getUnreadNotifications,
        patchNotification,
        markAllNotificationsAsRead,
        dismissNotification
    } = props;

    useEffect(() => {
        getUnreadNotifications();
        getNotifications();
        return () => { clearState(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const triggers = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
        const tips = triggers.map((el) => new Tooltip(el));
        return () => { tips.forEach(t => t.dispose()); };
    }, []);

    const visible: Notification[] = tab === 'unread' ? unreadNotifications : notifications;

    const grouped = useMemo(() => groupNotificationsByTime(visible), [visible]);

    const onRowClick = (item: Notification) => {
        if (!item.isRead) patchNotification(item.id);
        setTimeout(() => { window.location.href = item.redirect || '/notifications'; }, 200);
    };

    const onMarkOneRead = (e: React.MouseEvent, id: number) => {
        e.stopPropagation();
        patchNotification(id);
    };

    const onDismiss = (e: React.MouseEvent, id: number) => {
        e.stopPropagation();
        dismissNotification(id);
    };

    const renderRow = (n: Notification) => {
        const meta = TRIGGER_ICON[n.triggerType] ?? { icon: 'notification-bing', tone: 'primary' };
        return (
            <div
                key={`notif_${n.id}`}
                role='button'
                tabIndex={0}
                className={`app-notif-row ${n.isRead ? '' : 'is-unread'}`}
                onClick={() => onRowClick(n)}
                onKeyDown={(e) => { if (e.key === 'Enter') onRowClick(n); }}
            >
                <div className={`app-notif-row-icon bg-light-${meta.tone}`}>
                    <KTIcon iconName={meta.icon} className={`fs-2 text-${meta.tone}`} />
                </div>
                <div className='app-notif-row-body'>
                    <div className='d-flex align-items-center gap-2 min-w-0'>
                        <span className={`app-notif-row-title ${n.isRead ? 'text-gray-700' : 'text-gray-900 fw-bold'}`}>{n.subject}</span>
                        {!n.isRead && <span className='app-notif-dot' aria-hidden='true' />}
                    </div>
                    {n.body && <div className='text-gray-600 fs-7 app-notif-row-text'>{n.body}</div>}
                    <div className='text-gray-500 fs-8 mt-1'>{n.createdOnString}</div>
                </div>
                <div className='app-notif-row-actions'>
                    {!n.isRead && (
                        <button
                            type='button'
                            className='btn btn-icon btn-sm btn-light btn-active-light-primary'
                            aria-label={intl.formatMessage({ id: 'NOTIFICATION.ACTION.MARK_READ' })}
                            title={intl.formatMessage({ id: 'NOTIFICATION.ACTION.MARK_READ' })}
                            onClick={(e) => onMarkOneRead(e, n.id)}
                        >
                            <KTIcon iconName='check' className='fs-4' />
                        </button>
                    )}
                    <button
                        type='button'
                        className='btn btn-icon btn-sm btn-light btn-active-light-danger'
                        aria-label={intl.formatMessage({ id: 'NOTIFICATION.ACTION.DISMISS' })}
                        title={intl.formatMessage({ id: 'NOTIFICATION.ACTION.DISMISS' })}
                        onClick={(e) => onDismiss(e, n.id)}
                    >
                        <KTIcon iconName='cross' className='fs-4' />
                    </button>
                </div>
            </div>
        );
    };

    const renderGroup = (key: keyof typeof grouped, label: string) => {
        const items = grouped[key];
        if (items.length === 0) return null;
        return (
            <div key={key} className='app-notif-group'>
                <div className='app-notif-group-label'>{label}</div>
                {items.map(renderRow)}
            </div>
        );
    };

    const unreadCount = unreadNotifications.length;
    const unreadBadge = formatNavbarBadge(unreadCount);

    return (
        <div>
            <button
                type='button'
                data-kt-menu-trigger="{default: 'click'}"
                data-kt-menu-attach='parent'
                data-kt-menu-placement='bottom-end'
                className='app-navbar-trigger'
                data-bs-toggle='tooltip'
                title={intl.formatMessage({ id: 'NOTIFICATION.HEADER.NAME' })}
                aria-label={intl.formatMessage({ id: 'NOTIFICATION.HEADER.NAME' })}
            >
                <i className='ki-duotone ki-notification-status fs-1'>
                    <span className='path1'></span><span className='path2'></span><span className='path3'></span><span className='path4'></span><span className='path5'></span>
                </i>
                {unreadBadge && (
                    <span className='badge badge-danger app-navbar-badge'>{unreadBadge}</span>
                )}
            </button>

            <div
                className='menu menu-sub menu-sub-dropdown menu-column w-350px w-lg-400px app-notif-menu'
                data-kt-menu='true'
            >
                <div className='app-notif-header'>
                    <div className='d-flex align-items-center justify-content-between'>
                        <div className='min-w-0'>
                            <div className='fs-5 fw-bold text-gray-900'>
                                {intl.formatMessage({ id: 'NOTIFICATION.HEADER.NAME' })}
                            </div>
                            <div className='fs-7 text-gray-500'>
                                {unreadCount > 0
                                    ? intl.formatMessage({ id: 'NOTIFICATION.HEADER.UNREAD_COUNT' }, { count: unreadCount })
                                    : intl.formatMessage({ id: 'NOTIFICATION.EMPTY.UNREAD' })}
                            </div>
                        </div>
                        <div className='d-flex align-items-center gap-1'>
                            <button
                                type='button'
                                className='btn btn-icon btn-sm btn-active-light-primary'
                                disabled={unreadCount === 0}
                                aria-label={intl.formatMessage({ id: 'NOTIFICATION.ACTION.MARK_ALL_READ' })}
                                title={intl.formatMessage({ id: 'NOTIFICATION.ACTION.MARK_ALL_READ' })}
                                onClick={() => markAllNotificationsAsRead()}
                            >
                                <KTIcon iconName='double-check' className='fs-2' />
                            </button>
                            <Link
                                to='/profile'
                                className='btn btn-icon btn-sm btn-active-light-primary'
                                aria-label={intl.formatMessage({ id: 'NOTIFICATION.ACTION.SETTINGS' })}
                                title={intl.formatMessage({ id: 'NOTIFICATION.ACTION.SETTINGS' })}
                            >
                                <KTIcon iconName='setting-2' className='fs-2' />
                            </Link>
                        </div>
                    </div>

                    <div className='app-notif-tabs' role='tablist'>
                        <button
                            type='button'
                            role='tab'
                            aria-selected={tab === 'unread'}
                            className={`app-notif-tab ${tab === 'unread' ? 'active' : ''}`}
                            onClick={() => setTab('unread')}
                        >
                            {intl.formatMessage({ id: 'NOTIFICATION.TAB.UNREAD' })}
                            {unreadCount > 0 && <span className='app-notif-tab-count'>{unreadCount}</span>}
                        </button>
                        <button
                            type='button'
                            role='tab'
                            aria-selected={tab === 'all'}
                            className={`app-notif-tab ${tab === 'all' ? 'active' : ''}`}
                            onClick={() => setTab('all')}
                        >
                            {intl.formatMessage({ id: 'NOTIFICATION.TAB.ALL' })}
                        </button>
                    </div>
                </div>

                <div className='app-notif-list scroll-y mh-400px'>
                    {visible.length === 0 ? (
                        <div className='app-notif-empty'>
                            <div className='app-notif-empty-icon'>
                                <KTIcon iconName='notification-on' className='fs-2x text-gray-500' />
                            </div>
                            <div className='fw-semibold text-gray-700'>
                                {intl.formatMessage({ id: tab === 'unread' ? 'NOTIFICATION.EMPTY.UNREAD' : 'NOTIFICATION.EMPTY.ALL' })}
                            </div>
                        </div>
                    ) : (
                        <>
                            {renderGroup('today',     intl.formatMessage({ id: 'NOTIFICATION.GROUP.TODAY' }))}
                            {renderGroup('yesterday', intl.formatMessage({ id: 'NOTIFICATION.GROUP.YESTERDAY' }))}
                            {renderGroup('earlier',   intl.formatMessage({ id: 'NOTIFICATION.GROUP.EARLIER' }))}
                        </>
                    )}
                </div>

                <div className='app-notif-footer'>
                    <Link to='/notifications' className='btn btn-sm btn-light w-100 fw-semibold'>
                        {intl.formatMessage({ id: 'NOTIFICATION.HEADER.VIEW_ALL' })}
                        <KTIcon iconName='arrow-right' className='fs-5 ms-1' />
                    </Link>
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = (state: any) => ({
    notifications: state.Notification.notifications,
    unreadNotifications: state.Notification.unreadNotifications
});

const mapActionsToProps = {
    clearState,
    getNotifications,
    getUnreadNotifications,
    patchNotification,
    markAllNotificationsAsRead,
    dismissNotification
};

export default connect(mapStateToProps, mapActionsToProps)(HeaderNotificationsMenu);
